import { useEffect, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'

import { getSegmentation } from '../../../api/segmentations.js'
import { buildDisplayRowsFromPatterns } from './segmentationFormUtils.js'

export default function SegmentationDetailPage() {
  const { applicationId } = useOutletContext()
  const { segmentationId } = useParams()
  const [pageState, setPageState] = useState({
    status: 'loading',
    segmentation: null,
    error: null,
  })
  const [values, setValues] = useState(() => ({
    name: '',
    description: '',
    conditions: [],
    patternsText: '[]',
  }))

  useEffect(() => {
    let cancelled = false

    setPageState({
      status: 'loading',
      segmentation: null,
      error: null,
    })

    getSegmentation(applicationId, segmentationId)
      .then((segmentation) => {
        if (cancelled) return
        setPageState({
          status: 'ok',
          segmentation,
          error: null,
        })
        setValues({
          name: segmentation?.name || '',
          description: segmentation?.description || '',
          conditions: buildDisplayRowsFromPatterns(segmentation?.patterns || []),
          patternsText: JSON.stringify(segmentation?.patterns || [], null, 2),
        })
      })
      .catch((requestError) => {
        if (cancelled) return
        setPageState({
          status: 'error',
          segmentation: null,
          error: requestError,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, segmentationId])

  const segmentation = pageState.segmentation

  if (!segmentation) {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">
          {pageState.status === 'loading' ? 'Loading segmentation...' : 'Segmentation not found'}
        </div>
        {pageState.status === 'error' ? (
          <pre className="codeBlock codeBlockError">{String(pageState.error?.message || pageState.error)}</pre>
        ) : null}
        <div className="sectionActions">
          <Link to={`/applications/${applicationId}/segments`} className="button buttonGhost">
            Back to segments
          </Link>
        </div>
      </section>
    )
  }

  return (
    <div className="page">
      <div className="pageHeader">
        <div>
          <h1 className="pageTitle">Segmentation: {segmentation.name || 'Unnamed'}</h1>
        </div>
        <div className="buttonGroup">
          <Link to={`/applications/${applicationId}/segments`} className="button buttonGhost">
            Back
          </Link>
        </div>
      </div>

      <section className="card formCard">
        <div className="formGrid">
          <label className="formField">
            <span className="formLabel">Name</span>
            <input className="textInput" value={values.name} readOnly disabled />
          </label>
          <label className="formField">
            <span className="formLabel">Description</span>
            <input className="textInput" value={values.description} readOnly disabled />
          </label>
          <label className="formField">
            <span className="formLabel">Group config</span>
            <input
              className="textInput"
              value={segmentation.groupConfig?.name ?? '—'}
              readOnly
              disabled
            />
          </label>
          <div className="formField">
            <span className="formLabel">User parameters</span>
            {values.conditions.length === 0 ? (
              <div className="muted">No conditions.</div>
            ) : (
              <table className="table" aria-label="Segmentation conditions">
                <thead>
                  <tr>
                    <th>User parameter</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {values.conditions.map((condition, index) => (
                    <tr key={`${condition.label}_${index}`}>
                      <td>{condition.label}</td>
                      <td>{condition.value || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <label className="formField">
            <span className="formLabel">Patterns (JSON array)</span>
            <textarea
              className="textInput textAreaMonoLg"
              value={values.patternsText}
              readOnly
              disabled
            />
          </label>
        </div>
      </section>
    </div>
  )
}

